import googleAnalytics from "@analytics/google-analytics";
import { AuthenticationError } from "@auth0/auth0-react";
import { Channel, invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { attachConsole } from "@tauri-apps/plugin-log";
import { relaunch } from "@tauri-apps/plugin-process";
import { useMachine } from "@xstate/react";
import Analytics from "analytics";
import { isEmpty } from "lodash";
import NextApp, { type AppContext, type AppProps } from "next/app";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useEffectOnce, useInterval } from "react-use";
import { AnalyticsProvider } from "use-analytics";

import "@fontsource-variable/inter/wght.css";
import "@fontsource/ibm-plex-mono/latin.css";
import "@moment/design-system/main.css";
import "@xterm/xterm/css/xterm.css";

import { type AppConfig, appConfigSchema } from "@moment/api-collab/api-types";
import { generateShortUuid } from "@moment/api-collab/id";
import { useTheme } from "@moment/design-system/ThemeProvider";
import { ToastNotifications, makeToast } from "@moment/design-system/Toast";
import { type UserInfo, setUserProperty } from "@moment/logging";

import { type ClientActionsProps } from "~/ClientActions";
import { Providers } from "~/Providers";
import { getIframeBaseUrl, isDesktopAppMode } from "~/auth/interop/common";
import { onLoginSuccess } from "~/auth/loginAction";
import { useAuth } from "~/auth/useAuth";
import { useLogger } from "~/components/canvas/hooks/useLogger";
import { FatalAppError } from "~/components/errors/FatalAppError";
import { FindInPage } from "~/components/find-in-page/FindInPage";
import { useOutdatedAppModal } from "~/components/outdated-app/OutdatedAppModalContext";
import { Head } from "~/components/seo/Head";
import { exists, getConfigFilePath, readJsonFile, writeJsonFile } from "~/data/filesystem";
import { useAtlasSettingsState } from "~/state";
import { useAppActor } from "~/state/machines/app/app.machine";
import { autoUpdaterMachine } from "~/state/machines/desktop/autoupdater.machine";
import { type DownloadEvent } from "~/state/types";
import { HOMEPAGE_ROUTE } from "~/strings";
import { newFetch } from "~/utils/atlas";
import { initLogger } from "~/utils/datadog/datadog";
import { isLocal } from "~/utils/env";
import { type MessageDataArgs, isRunningInIframe } from "~/utils/iframe";
import { printEnvironment } from "~/utils/printEnvironment";

export type MomentAppProps = AppProps & {
	cookies?: string;
};

const ClientActions = dynamic<ClientActionsProps>(
	() => import("~/ClientActions").then((m) => m.ClientActions),
	// turn off server side rendering for this code
	{ ssr: false }
);

const App = (props: MomentAppProps) => {
	const { pageProps } = props;

	return (
		<>
			<Head
				// pageProps comes in as "any" so we need to check for existence
				title={pageProps?.title ?? null}
				description={pageProps?.description ?? null}
				url={pageProps?.url ?? null}
			/>
			<ClientActions />
			<ErrorBoundary FallbackComponent={FatalAppError}>
				<Providers pageProps={pageProps}>
					<Main {...props} />
					{/* <SpeedInsights /> */}
				</Providers>
			</ErrorBoundary>
		</>
	);
};

const getMeasurementIds = () => {
	if (process.env.APP_ENV === "production") {
		return "G-PBWVPLX26V";
	} else {
		return "G-4JR0PBCCBT";
	}
};

const analytics = Analytics({
	app: "Moment App",
	plugins: [
		googleAnalytics({
			measurementIds: getMeasurementIds(),
		}),
	],
});

// Use a variable to prevent running
// init routine for desktop app twice.
// https://react.dev/learn/you-might-not-need-an-effect#initializing-the-application
let didRunDesktopAppInit = false;

interface UpdateMetadata {
	latestVersion: string;
	currentVersion: string;
}

/**
 * Returns true if the current location matches
 * the pattern app-git-*-moment-eng.vercel.app.
 */
// function isCurrentLocationVercelBranchUrl() {
// 	return window.location.hostname.match("app-git-.*-moment-eng.vercel.app");
// }

export const Main = ({ Component, pageProps }: AppProps) => {
	const { confirm } = useOutdatedAppModal();
	const [showFindInPage, setShowFindInPage] = useState(false);
	const {
		isAuthenticated,
		loginWithRedirect,
		getAccessTokenSilently,
		error: authError,
		user,
		getIdTokenClaims,
		logoutWithRedirect,
		_Auth0User,
	} = useAuth();
	const router = useRouter();
	const iframeBaseUrl = getIframeBaseUrl(process.env.APP_ENV);

	const appActor = useAppActor();

	const atlasSettings = useAtlasSettingsState();

	// we use this to save the original fetch function.
	// we need to do this because we want to fallback to the original fetch function
	// if the user disables proxying via atlas..
	const [originalFetch] = useState(() => {
		return globalThis.fetch;
	});

	const [autoUpdaterState, sendToAutoUpdater] = useMachine(autoUpdaterMachine);

	const logger = useLogger("Main");
	const { setTheme } = useTheme();

	const onMessageFromIframe = useCallback(
		async (event: MessageEvent) => {
			if (event.origin !== iframeBaseUrl) {
				console.warn("Received message from unrecognized origin. Ignoring.", event);
				return;
			}

			const data: MessageDataArgs = event.data;
			const port = event.ports?.[0];

			switch (data.type) {
				case "ping":
					{
						port?.postMessage(window.location.origin);
					}
					break;

				case "loginWithRedirect":
					{
						void loginWithRedirect({ redirectPath: window.location.pathname });
					}
					break;
				case "getIdTokenClaims":
					{
						if (!port) {
							console.error(`A message port is required for ${data.type}`);
							return;
						}
						const claims = await getIdTokenClaims();
						port.postMessage(claims);
					}
					break;
				case "getAccessTokenSilently":
					{
						if (!port) {
							console.error(`A message port is required for ${data.type}`);
							return;
						}

						const token = await getAccessTokenSilently();
						port.postMessage(token);
					}
					break;
				case "getAuth0User":
					{
						if (!port) {
							console.error(`A message port is required for ${data.type}`);
							return;
						}
						port.postMessage(_Auth0User);
					}
					break;
				case "isAuthenticated":
					{
						if (!port) {
							console.error(`A message port is required for ${data.type}`);
							return;
						}
						port.postMessage(isAuthenticated);
						port.close();
					}
					break;
				case "logoutWithRedirect":
					{
						void logoutWithRedirect(window.location.pathname);
					}
					break;
				case "setTheme":
					{
						const args = data.args;
						if (!args?.["theme"]) {
							console.error("No theme value provided by iframe");
							return;
						}

						setTheme(args["theme"]);
					}
					break;
				/*
				UPDATE - as of 5/15/2025 - turns out proxying via reboot would be more work than expected.
					reboot uses a persistent connection. So the parent frame would need to manage that 
					persistent connection, transform that data, and send that data to the child iframe.

					Praneet suggests not to use the user's GH/Auth0 access token with Reboot. 
					But rather evaluate using a custom JWT token scoped to just Reboot that we can hand to the iframe. 
					It would then allow the iframe to only use it for calling specific things like Reboot.

				TODO - as of 5/9/2025, iframes are only used in the browser/nextjs app.
					below is an example of how the parent frame can proxy a request,
					where the child iframe asks the parent frame to write data to DocStorage
					and the parent frame sends the request to reboot with the access token.
					then sends the response back to the child iframe.

					Note that below is just a prototype and not to be used exactly as is.
				case "putDocStorageData":
					{
						if (!port) {
							console.error(`A message port is required for ${data.type}`);
							return;
						}

						const accessToken = await getAccessTokenSilently();

						// we need to be strict on what the child iframe is allowed to request.
						if (data.url.hostname !== "reboot.moment.dev") {
							port.postMessage(new Error("Invalid hostname"));
							return;
						}
						
						const resp = fetch(data.url, {
							body: data.body,
							headers: {
								Authorization: `Bearer ${accessToken}`,
								"Content-Type": "application/json",
							},
							method: "PUT",
						})

						if (resp.ok) {
							port.postMessage(resp.json());
						} else {
							port.postMessage(new Error("Failed to write data"));
						}

					}
					break;
				*/
			}

			port?.close();
		},
		[
			isAuthenticated,
			getIdTokenClaims,
			loginWithRedirect,
			logoutWithRedirect,
			getAccessTokenSilently,
			_Auth0User,
			iframeBaseUrl,
			setTheme,
		]
	);

	useEffect(() => {
		// Only setup a message listener for web platform.
		if (isDesktopAppMode()) {
			return;
		}
		// // Setup a message listener when the app is
		// // running as the parent frame.
		// //
		// // The current window's (i.e. the parent frame)
		// // hostname must be one of:
		// // - docs.moment.dev
		// // - docs-staging.moment.dev
		// // - www.moment.dev
		// // - app-git-*-moment-eng.vercel.app
		// if (
		// 	!window.location.hostname.startsWith(PROD_DOCS_HOSTNAME) &&
		// 	!PROD_MARKETING_HOSTNAMES.has(window.location.hostname) &&
		// 	!window.location.hostname.startsWith(STAGING_DOCS_HOSTNAME) &&
		// 	!window.location.hostname.startsWith(STAGING_MOMENT_HOSTNAME) &&
		// 	!isCurrentLocationVercelBranchUrl()
		// ) {
		// 	return;
		// }

		// The message callback should be assigned to the parent frame.
		if (isRunningInIframe()) {
			return;
		}

		window.onmessage = onMessageFromIframe;
	}, [onMessageFromIframe]);

	const fetchUpdate = useCallback(async () => {
		const onEvent = new Channel<DownloadEvent>((message) => {
			sendToAutoUpdater({ type: "update", message });
		});

		let updateMetadata: UpdateMetadata | undefined = undefined;
		try {
			// We don't set endpoints for local mode for auto-updating.
			// THus to avoid unnecessary error logs, we skip this check.
			// if we need to test auto-updating,
			// we need to set an endpoint in tauri.conf.json.
			if (isLocal) {
				return;
			}
			const value = await invoke("fetch_update");
			if (!value) {
				return;
			}

			updateMetadata = value as UpdateMetadata;
			if (updateMetadata.latestVersion === updateMetadata.currentVersion) {
				sendToAutoUpdater({ type: "reset" });
				return;
			}

			logger.info("Update is available", {
				currentVersion: updateMetadata.currentVersion,
				latestVersion: updateMetadata.latestVersion,
			});
		} catch (err) {
			console.error("Failed to check for updates", err);
		}

		if (!updateMetadata) {
			return;
		}

		try {
			const traceId = `tauri-updater_${generateShortUuid()}`;
			await invoke("prepare_update", {
				onEvent,
				traceId,
				versionDownloading: updateMetadata.latestVersion,
			});
		} catch (err) {
			console.error("Failed to download update", err);
		}
	}, [sendToAutoUpdater, logger]);

	useEffect(() => {
		if (!isDesktopAppMode()) {
			return;
		}

		if (didRunDesktopAppInit) {
			return;
		}

		didRunDesktopAppInit = true;
		void attachConsole();

		void listen("moment-new-document", async (event) => {
			await router.push(HOMEPAGE_ROUTE);
			const payload: {
				title?: string;
				body?: string;
				bodyType?: "html" | "text" | undefined;
			} = ("payload" in event && event.payload) || {};
			appActor.send({
				type: "documentList.createDocument",
				payload,
			});
		});
		void listen("moment-open-document", async (event) => {
			const {
				workspaceName,
				documentId,
			}: {
				workspaceName?: string;
				documentId?: string;
			} = ("payload" in event && event.payload) || {};
			if (!workspaceName || !documentId) {
				return;
			}
			await router.push(HOMEPAGE_ROUTE);
			// TODO: This can take you to missing documents in some edge cases...
			// I think to fix this we really want to route a message through the app machine
			await router.push(`${HOMEPAGE_ROUTE}/${workspaceName}/${documentId}`);
		});
		void listen("show-find-in-page", () => setShowFindInPage(true));
		void listen("check-for-updates", () => fetchUpdate());
		void fetchUpdate();

		void listen("show-error", (event) => {
			const message =
				"payload" in event && event.payload && event.payload["message"]
					? event.payload["message"]
					: "unknown error";
			makeToast({
				message,
				variant: "error",
			});
		});
	}, [appActor, fetchUpdate, loginWithRedirect, router]);

	// TODO: move this to the tauri layer
	useInterval(
		() => {
			fetchUpdate().catch(logger.error);
		},
		1000 * 60 * 60
	);

	// handle loading config.
	// we only really seem to use this for the anonID, which is used for datadog.
	// originally, I wanted to read this via createQueryMachine,
	// but was fighting with the types way too much.
	useEffectOnce(() => {
		if (!isDesktopAppMode()) {
			return;
		}

		void (async () => {
			const configPath = await getConfigFilePath();
			const configExists = await exists(configPath);
			if (!configExists) {
				// create an anonymous user ID
				const config = appConfigSchema.parse({});
				setUserProperty("anonID", config.userID);
				await writeJsonFile(configPath, config);
				return;
			}

			const config = await readJsonFile<AppConfig>(configPath);
			const anonUserID = config?.userID;
			if (!isEmpty(anonUserID)) {
				setUserProperty("anonID", anonUserID);
			}
		})();
	});

	// reload fetch if atlas proxy settings changes
	useEffect(() => {
		if (originalFetch === undefined) {
			return;
		}

		// we do not support the browser as of today.
		if (!isDesktopAppMode()) {
			return;
		}

		globalThis.fetch = newFetch(atlasSettings.settings, originalFetch);
		console.debug("AtlasProvider: updated fetch function", atlasSettings.settings);
	}, [atlasSettings.settings, originalFetch]);

	useEffect(() => {
		const datadogUser: UserInfo = {
			id: "",
			email: "",
			isMoment: false,
			loggedIn: false,
		};

		if (!isEmpty(user)) {
			datadogUser.id = user.id;
			datadogUser.loggedIn = true;
			datadogUser.isMoment = user.isMomentEmployee;
			datadogUser.email = user.email;
			datadogUser.name = user.name;
			void analytics.identify(user.id, {
				isDesktop: isDesktopAppMode(),
			});
		}

		if (datadogUser.id !== "") {
			setUserProperty("id", datadogUser.id);
			setUserProperty("email", datadogUser.email);
			setUserProperty("isMoment", datadogUser.isMoment);
			setUserProperty("loggedIn", datadogUser.loggedIn);
			setUserProperty("name", datadogUser.name);
		}
	}, [user]);

	// Watch for postlogin failure
	useEffect(() => {
		const { postlogin, postlogout } = router.query;
		if (postlogin === "true" || postlogout === "true") {
			if (authError) {
				console.log(authError);
				makeToast({
					message:
						"There was an error with your login. Please try again or contact support@moment.dev",
					timer: "dismissible",
					variant: "error",
				});

				if (postlogin === "true" && authError instanceof AuthenticationError) {
					const auth0Error = authError as AuthenticationError;
					const returnTo = onLoginSuccess(auth0Error.appState["redirectId"]);
					if (returnTo) {
						void router.replace({ query: {}, pathname: returnTo }, undefined, {
							shallow: true,
						});
					}
				}
			}
		}
	}, [authError, router]);

	useEffectOnce(() => {
		printEnvironment();
	});

	useEffect(() => {
		if (!autoUpdaterState.context.autoUpdaterState.isOutdatedAppVersion) {
			return;
		}

		confirm()
			.then(async (confirmed) => {
				if (!confirmed) {
					return;
				}

				// relaunch BYPASSES shutdown hooks. NOT OKAY.
				await invoke("stop_atlas");
				await relaunch();
			})
			.catch((err) =>
				logger.error("Failed to show dialog for min version enforcement", {
					error: `${err}`,
				})
			);
	}, [confirm, logger, autoUpdaterState]);

	useEffectOnce(() => {
		initLogger();
	});

	return (
		<AnalyticsProvider instance={analytics}>
			{showFindInPage && <FindInPage onClose={() => setShowFindInPage(false)} />}
			<Component {...pageProps} />
			<ToastNotifications />
		</AnalyticsProvider>
	);
};

App.getInitialProps = async (context: AppContext) => {
	const appProps = NextApp.getInitialProps(context);

	return { ...appProps, cookies: context.ctx.req?.headers.cookie };
};

export default App;
